import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isUuid } from "utils/utils";
import { ReferenceLabelEntry, referenceLabelSelector } from "./ReferenceLabel.selectors";
import { enqueueReferenceLabelRequest } from "./ReferenceLabel.service";

/**
 * Resolve a read-only reference cell's display label (id -> name) through the
 * shared, batched resolver cache. Pass `resource === undefined` (e.g. for
 * name-stored references, whose value is already the display name) to skip
 * resolution entirely. Only UUID-valued ids are resolved; anything else is left
 * to the caller to render as-is.
 *
 * Returns the cache entry (`{ status, label }`) or `undefined` while nothing has
 * been requested yet. The caller falls back to the raw value when the entry is
 * missing, still loading, or resolved to a null label.
 */
export const useReferenceLabel = (
    resource?: string,
    id?: string
): ReferenceLabelEntry | undefined => {
    const dispatch = useDispatch();
    const shouldResolve = Boolean(resource) && Boolean(id) && isUuid(id as string);

    const entry = useSelector(
        referenceLabelSelector(
            shouldResolve ? resource : undefined,
            shouldResolve ? id : undefined
        )
    );

    useEffect(() => {
        if (!shouldResolve || entry !== undefined) {
            return;
        }
        enqueueReferenceLabelRequest(resource as string, id as string, dispatch);
    }, [shouldResolve, resource, id, entry, dispatch]);

    return entry;
};
